import { Stack, Text, Center } from '@mantine/core';
import { ClipboardList } from 'lucide-react';
import { AttendanceToolbar } from './AttendanceToolbar';
import { AttendanceTable } from './AttendanceTable';
import { useDataLibraryAttendance } from './useDataLibraryAttendance';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';

interface DataLibraryAttendanceTabProps {
  selectedCohortIds: string[];
  /** MED-661: Initial group-by state when opened from drill-down */
  initialGroup?: GroupState | null;
  /** MED-773: Initial filters when opened from drill-down with group selection */
  initialFilters?: ActiveFilterRow[];
}

export function DataLibraryAttendanceTab({
  selectedCohortIds,
  initialGroup,
  initialFilters,
}: DataLibraryAttendanceTabProps) {
  const {
    data,
    groupedData,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize,
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeQuickFilters,
    handleQuickFilterToggle,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    activeGroup,
    setActiveGroup,
    columns,
    setColumns,
    visibleColumns,
    groupLimitTotal,
  } = useDataLibraryAttendance({ selectedCohortIds, initialGroup, initialFilters });

  if (selectedCohortIds.length === 0) {
    return (
      <Center h={300}>
        <Stack align="center" gap="sm">
          <ClipboardList size={40} color="#888" />
          <Text c="dimmed" size="lg">
            Select one or more cohorts to view attendance
          </Text>
          <Text c="dimmed" size="sm">
            Use the side panel to choose cohorts
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="sm">
      <AttendanceToolbar
        groupLimitTotal={groupLimitTotal}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        filterOptions={filterOptions}
        activeQuickFilters={activeQuickFilters}
        onQuickFilterToggle={handleQuickFilterToggle}
        activeFilters={activeFilters}
        onFilterAdd={handleFilterAdd}
        onFilterChange={handleFilterChange}
        onFilterRemove={handleFilterRemove}
        onClearAll={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
        sortConfig={sortConfig}
        onSortChange={handleSortPanelChange}
        activeGroup={activeGroup}
        onGroupChange={setActiveGroup}
        columns={columns}
        onColumnsChange={setColumns}
        totalCount={totalCount}
        selectedCohortIds={selectedCohortIds}
      />

      <AttendanceTable
        data={data}
        averages={averages}
        groupedData={groupedData}
        columns={visibleColumns}
        sortConfig={sortConfig}
        onSort={handleSort}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearAllFilters}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        hidePagination={!!groupedData}
      />
    </Stack>
  );
}
